import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface GridActivityComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

export const GridActivityComponent: React.FC<GridActivityComponentProps> = ({ resource, component, onAction }) => {
  const { t } = useTranslation();
  const [selectedCells, setSelectedCells] = useState<number[]>([]);
  const [currentMode, ] = useState(resource.mode || 'read_and_select');

  // Reset component state when resource changes (for quiz navigation)
  useEffect(() => {
    setSelectedCells([]);
  }, [resource.id]);

  const rows = resource.rows || 4;
  const cols = resource.cols || 6;
  const bank = resource.bank || [];
  const totalCells = rows * cols;

  const handleCellClick = (index: number) => {
    if (currentMode === 'read_and_select') {
      setSelectedCells(prev => 
        prev.includes(index) 
          ? prev.filter(i => i !== index)
          : [...prev, index]
      );
      onAction?.('cell_selected', { index, selected: !selectedCells.includes(index) });
    }
  };

  const handleReset = () => {
    setSelectedCells([]);
    onAction?.('reset_grid', {});
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">{t('gridActivity.gridActivity')}</h3>
        <p className="text-sm text-gray-600 capitalize">
          {t('gridActivity.mode')}: {currentMode.replace('_', ' ')}
        </p>
      </div>

      <div className="grid gap-1 mb-4" style={{ 
        gridTemplateColumns: `repeat(${cols}, 1fr)` 
      }}>
        {Array.from({ length: totalCells }, (_, index) => (
          <button
            key={index}
            onClick={() => handleCellClick(index)}
            className={`w-12 h-12 rounded-lg border-2 transition-all ${
              selectedCells.includes(index)
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400 bg-gray-50'
            }`}
          >
            {bank[index] || index + 1}
          </button>
        ))}
      </div>

      <div className="flex justify-center space-x-2">
        <button
          onClick={handleReset}
          className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          {t('gridActivity.reset')}
        </button>
        <button
          onClick={() => setSelectedCells([])}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('gridActivity.clearSelection')}
        </button>
      </div>

      <div className="mt-3 text-center text-sm text-gray-600">
        {t('gridActivity.selected')}: {selectedCells.length} {t('gridActivity.cells')}
      </div>
    </div>
  );
};
